import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { useAuth } from '@/contexts/AuthContext';

declare global {
  interface Window {
    dataLayer?: any[];
    gtag: (...args: any[]) => void;
  }
}

export function GoogleAnalytics() {
  const { settings } = useSiteSettings();
  const { user } = useAuth();
  const location = useLocation();
  const gaId = settings.google_analytics_id;
  const gaEnabled = settings.google_analytics_enabled;
  const canonicalDomain = settings.canonical_domain;

  const GA_CLIENT_ID_KEY = 'cinerig_ga_client_id';

  // Track last-sent path to deduplicate page_view on initial load
  const lastSentPathRef = useRef<string | null>(null);

  // Detect Lovable preview environment
  const isLovablePreview = window.location.hostname.includes('lovableproject.com') || 
                           window.location.hostname.includes('localhost') ||
                           window.location.hostname.includes('webcontainer');

  // Only load GA on the canonical production domain (e.g., cinerig.pro)
  const canonicalHost = (() => {
    try {
      return canonicalDomain ? new URL(canonicalDomain).hostname : '';
    } catch {
      return '';
    }
  })();

  const isOnCanonicalDomain = (() => {
    if (!canonicalHost) return true; // If not configured, don't block
    const host = window.location.hostname;
    return host === canonicalHost || host === `www.${canonicalHost}`;
  })();

  // Skip GA for admin routes, Lovable preview, if disabled, or if no GA ID configured
  const shouldLoadGA =
    gaEnabled &&
    gaId &&
    !location.pathname.startsWith('/admin') &&
    !isLovablePreview &&
    isOnCanonicalDomain;

  const [gtagReady, setGtagReady] = useState(false);
  const readyTimerRef = useRef<number | null>(null);

  const gtagScriptSrc = useMemo(() => {
    if (!gaId) return null;
    return `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  }, [gaId]);

  function ensureGtagStub() {
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() {
      window.dataLayer?.push(arguments);
    };
  }

  function startReadyProbe() {
    if (readyTimerRef.current) window.clearInterval(readyTimerRef.current);
    readyTimerRef.current = window.setInterval(() => {
      const ok = typeof window.gtag === 'function';
      if (ok) {
        setGtagReady(true);
        if (readyTimerRef.current) window.clearInterval(readyTimerRef.current);
        readyTimerRef.current = null;
      }
    }, 100);
  }

  // Dev-only visibility into why GA is not loading (helps debugging "Realtime shows 0")
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (shouldLoadGA) return;

    const reasons: string[] = [];
    if (!gaEnabled) reasons.push('disabled');
    if (!gaId) reasons.push('missing measurement ID');
    if (location.pathname.startsWith('/admin')) reasons.push('admin route');
    if (isLovablePreview) reasons.push('preview environment');
    if (!isOnCanonicalDomain) reasons.push('not canonical domain');

    // eslint-disable-next-line no-console
    console.info('[GA] not loading:', reasons.join(', ') || 'unknown');
  }, [shouldLoadGA, gaEnabled, gaId, location.pathname, isLovablePreview, isOnCanonicalDomain]);

  // Load GA script and send initial config
  useEffect(() => {
    if (!shouldLoadGA || !gaId) return;

    ensureGtagStub();
    startReadyProbe();

    const currentPath = location.pathname + location.search;

    // Check if script already exists
    if (document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`)) {
      window.gtag('js', new Date());
      window.gtag('config', gaId, { page_path: currentPath, send_page_view: false });
      return () => {
        if (readyTimerRef.current) window.clearInterval(readyTimerRef.current);
        readyTimerRef.current = null;
      };
    }

    window.gtag('js', new Date());
    // Disable automatic page_view on config to prevent duplicates; we send manually below
    window.gtag('config', gaId, { page_path: currentPath, send_page_view: false });

    // Load gtag.js script
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    script.async = true;
    script.onload = () => setGtagReady(true);
    document.head.appendChild(script);

    return () => {
      if (readyTimerRef.current) window.clearInterval(readyTimerRef.current);
      readyTimerRef.current = null;
    };
  }, [gaId, shouldLoadGA]);

  // Set GA User-ID when authenticated (for user-level joins via GA export)
  useEffect(() => {
    if (!shouldLoadGA || !gaId || !gtagReady || !window.gtag) return;
    const userId = user?.id ? String(user.id) : undefined;
    window.gtag('set', { user_id: userId });
  }, [shouldLoadGA, gaId, user?.id, gtagReady]);

  // Capture GA client_id and cache for first-party session inserts
  useEffect(() => {
    if (!shouldLoadGA || !gaId || !gtagReady || !window.gtag) return;
    try {
      window.gtag('get', gaId, 'client_id', (clientId: string) => {
        if (!clientId) return;
        try {
          localStorage.setItem(GA_CLIENT_ID_KEY, clientId);
        } catch {
          // ignore
        }
      });
    } catch {
      // ignore
    }
  }, [shouldLoadGA, gaId, gtagReady]);

  // Track page views on route change (deduplicated)
  useEffect(() => {
    if (!shouldLoadGA || !gaId || !gtagReady || !window.gtag) return;

    const currentPath = location.pathname + location.search;
    if (lastSentPathRef.current === currentPath) return;
    lastSentPathRef.current = currentPath;

    window.gtag('event', 'page_view', {
      page_path: currentPath,
      send_to: gaId,
    });
  }, [location.pathname, location.search, gaId, shouldLoadGA, gtagReady]);

  // Expose a tiny debug breadcrumb for Diagnostics overlay
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!shouldLoadGA) return;
    // eslint-disable-next-line no-console
    console.info('[GA] init', {
      host: window.location.hostname,
      script: gtagScriptSrc,
      gtagReady,
    });
  }, [gtagReady, shouldLoadGA, gtagScriptSrc]);

  return null;
}
